import React, { useEffect, useState } from "react";
import Wrapper from "../assets/wrappers/DashboardHome";
import { RootState } from "../store";
import { useSelector, useDispatch } from "react-redux";
import { fetchMeetingTypes } from "../features/upload/uploadSlice";
import { getFinancialYr, setCurrentClickedMenu, setMeetingType, setSelectedFinYear } from "../features/common/commonSlice";
import Select from "react-select";
import axios from "axios";
import { Bar, Doughnut } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

const FaChartLine = require('react-icons/fa').FaChartLine;

const DashboardHome: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [loadingKpi, setLoadingKpi] = useState(true);
    const { meetingTypes, loading, MeetingTypeFetched } = useSelector(
        (state: RootState) => state.upload
    );
    const { financialYr, currentFinYear, meetingType } = useSelector(
        (state: RootState) => state.common
    );
    const [finYear, setFinYear] = useState<string>("");
    const [selectedMeeting, setSelectedMeeting] = useState<any>(
        meetingType || { value: 0, label: "All Meeting Types" }
    );
    const [kpis, setKpis] = useState<any[]>([]);
    const [languageTable, setLanguageTableData] = useState<any[]>([]);
    const baseUrl = process.env.REACT_APP_BASE_URL;

    useEffect(() => {
        if (!MeetingTypeFetched && !loading) {
            dispatch(fetchMeetingTypes() as any);
        }
    }, [MeetingTypeFetched, loading, dispatch]);

    useEffect(() => {
        dispatch(getFinancialYr());
    }, [dispatch]);

    const finYearOptions = [
        { value: "", label: "All" },
        ...financialYr.map((year) => ({
            value: year,
            label: year,
        })),
    ];

    const meetingTypeOptions = [
        {
            label: "All Meeting Types",
            options: [
                {
                    value: 0,
                    label: "All Meeting Types",
                },
            ],
        },
        ...(meetingTypes?.map((parent) => ({
            label: parent.name,
            options: parent.sub_options.map((sub: any) => ({
                value: sub.id,
                label: sub.name,
            })),
        })) || []),
    ];


    useEffect(() => {
        fetchDashboardData(finYear, selectedMeeting?.value || 0);
    }, [finYear, selectedMeeting]);

    const fetchDashboardData = async (finYear: string, meetingType: string | number) => {
        try {
            setLoadingKpi(true);

            const response = await axios.get(
                `${baseUrl}api/dashboard?fin_year=${encodeURIComponent(finYear)}&meeting_type=${encodeURIComponent(meetingType || 0)}`
            );

            if (response?.data?.msg_code === 400) {
                toast.error(response?.data?.msg || "Something went wrong");
                setKpis([]);
                setLanguageTableData([]);
                return;
            }
            setKpis(response?.data?.kpis || []);
            setLanguageTableData(response?.data?.language_table || []);
        } catch (error) {
            console.error("Error fetching dashboard data:", error);
            setKpis([]);
            setLanguageTableData([]);
        } finally {
            setLoadingKpi(false);
        }
    };

    const getKpiValue = (title: string) => {
        if (loadingKpi) return "--";
        const kpi = kpis.find((item) => item.title === title);
        return kpi?.value ?? "NA";
    };

    // Get top 10 by total_meetings
    const top10Languages = [...languageTable]
        .sort((a, b) => b.total_meetings - a.total_meetings)
        .slice(0, 10);

    const barChartData = {
        labels: top10Languages.map((item) => item.meeting_language),
        datasets: [
            {
                data: top10Languages.map((item) => item.total_meetings),
                backgroundColor: [
                    "#024a9c",
                    "#c7436f",
                    "#24a809",
                    "#5fa19c",
                    "#236a61",
                    "#d4a832",
                    "#7e4693",
                    "#c56a1c",
                    "#159e87",
                    "#e05574"
                ]
            },
        ],
    };

    // chart options
    const optionsChart = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    label: function (context: any) {
                        return `${context.label}: ${context.parsed.y}`;
                    },
                },
            },
        },
        scales: {
            y: { beginAtZero: true },
        },
    };

    const handleCardClick = (menuName: string) => {
        dispatch(setCurrentClickedMenu(menuName));
        navigate("/dashboard-landing");
    };

    const centerTextPlugin = {
    id: 'centerText',
    beforeDraw(chart: any) {
        const { width, height, ctx } = chart;
        ctx.restore();
        const total = chart.data.datasets[0].data.reduce((a: number, b: number) => a + b, 0);
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = '#4F4F4F';
        ctx.font = '11px sans-serif';
        ctx.fillText("Total Meetings", width / 2, height / 2 - 10);
        ctx.fillStyle = '#111827';
        ctx.font = 'bold 15px sans-serif';
        ctx.fillText(total.toString(), width / 2, height / 2 + 15);
        ctx.save();
    }
};

    const donutData = {
        labels: ["Final Submitted", "Draft"],
        datasets: [{
            data: [
                kpis.find(k => k.title === "final_submitted_mom")?.value || 0,
                kpis.find(k => k.title === "draft_mom")?.value || 0,
            ],
            backgroundColor: ["#9d4edd", "#ffee32"],
            borderWidth: 0,
            cutout: '75%',
        }],
    };

    const donutOptions = {
        plugins: {
            legend: { display: false },
            tooltip: { enabled: true }
        },
        maintainAspectRatio: false,
    };
    return (
        <Wrapper>
            <div className="row items-center box-shadow1 mt-3">
                <div className="dashboard-header flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-0">
                    <h2 className="dashboard-heading mb-2 md:mb-0">Dashboard</h2>
                    <div className="handleDropdown flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                        <Select
                            className="w-full sm:w-64"
                            placeholder="Select Financial Year"
                            value={finYearOptions.find((opt) => opt.value === finYear)}
                            onChange={(selected) => {
                                setFinYear(selected?.value || "");
                                dispatch(setSelectedFinYear(selected?.value || ""))
                            }}
                            options={finYearOptions}
                            isSearchable={false}
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    borderRadius: "0.5rem",
                                    boxShadow: "none",
                                    borderColor: "#ccc",
                                }),
                            }}
                        />
                        <Select
                            className="w-full sm:w-64"
                            placeholder="Select Meeting Type"
                            value={selectedMeeting}
                            onChange={(selected) => {
                                setSelectedMeeting(selected);
                                dispatch(setMeetingType(selected));
                            }}
                            options={meetingTypeOptions}
                            isSearchable={true}
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    borderRadius: "0.5rem",
                                    boxShadow: "none",
                                    borderColor: "#ccc",
                                }),
                                menu: (base) => ({
                                    ...base,
                                    minWidth: "250px",
                                }),
                            }}
                        />
                    </div>
                </div>


                {/* KPIs Cards */}


                <div className="row mt-4">
                    <div className="col-md-6">
                        <div className="FourCardGrid mt-3">
                            <div className="center-circle-card">
                                <Doughnut
                                    data={donutData}
                                    options={donutOptions}
                                    plugins={[centerTextPlugin]}
                                />
                            </div>

                            <div className="grid-item-dash purple-bg" onClick={() => handleCardClick("Gram Panchayats")}>
                                <div className="grid-item-dash-top">
                                    <span>Gram Panchayats</span>
                                    <div className="grid-item-dash-top-circle purple-cir"><FaChartLine /></div>
                                </div>
                                <p className="grid-item-dash-numb">{getKpiValue("unique_gps")}</p>
                            </div>
                            <div className="grid-item-dash green-bg" onClick={() => handleCardClick("Block Panchayats")}>
                                <div className="grid-item-dash-top">
                                    <span>Block Panchayats</span>
                                    <div className="grid-item-dash-top-circle green-cir"><FaChartLine /></div>
                                </div>
                                <p className="grid-item-dash-numb">{getKpiValue("unique_bps")}</p>
                            </div>
                            <div className="grid-item-dash blue-bg" onClick={() => handleCardClick("Zilla Panchayats")}>
                                <div className="grid-item-dash-top" >
                                    <span>Zilla Panchayats</span>
                                    <div className="grid-item-dash-top-circle blue-cir"><FaChartLine /></div>
                                </div>
                                <p className="grid-item-dash-numb">{getKpiValue("unique_zps")}</p>
                            </div>
                            <div className="grid-item-dash skyBlue-bg" onClick={() => handleCardClick("States")}>
                                <div className="grid-item-dash-top">
                                    <span>States</span>
                                    <div className="grid-item-dash-top-circle skyBlue-cir"><FaChartLine /></div>
                                </div>
                                <p className="grid-item-dash-numb">{getKpiValue("unique_states")}</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6">

                        <div className="d-flex justify-content-between align-items-center ">
                            <h5 className="mb-0">Multilingual Coverage</h5>
                            <div
                                className="grid-item-dash-top-circle yellow-cir" onClick={() => handleCardClick("Multilingual Coverage")}
                            >
                                <FaChartLine />
                            </div>
                        </div>
                        <div style={{ width: "100%", height: "300px", background: "#efefef" }}>
                            {languageTable.length > 0 ? (
                                <Bar data={barChartData} options={optionsChart} />

                            ) : (
                                <div className="d-flex justify-content-center align-items-center h-100">
                                    <p className="text-muted text-big mb-0">No data available</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

            </div>
        </Wrapper>
    );
};

export default DashboardHome;
